import { Close } from '@mui/icons-material';
import { Button, Divider } from '@mui/material';
import { FC, FormEventHandler, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';

import { CancelConfirmation } from 'pages/new-transaction/dialogs';

import { useSizeTypes } from 'contexts';
import EmailSelect from 'pages/admin/customers/email-select';
import { TransactionStatus } from 'pages/new-transaction/new-transaction.context';
import { Chassis, Genset, isChassis, isGenset } from 'types';
import { useDamage, useNewTransaction, useTireInspections } from '../new-transaction';
import '../new-transaction/new-transaction.scss';
import './review-transaction.scss';

const KeyValuePair: FC<PropsWithChildren<{ title: string }>> = ({ children, title }) => (
  <div className="key-value-pair d-flex justify-content-between g-5">
    <p className="property-name font-weight-bold">{title}</p>
    <p className="property-value font-weight-bold">{children ?? ''}</p>
  </div>
);

const ChassisReview: FC<{ chassis: Chassis }> = ({ chassis }) => (
  <>
    <KeyValuePair title="Chassis Number">{chassis.unitNumber}</KeyValuePair>
    <KeyValuePair title="Chassis Size Type">{chassis.sizeType}</KeyValuePair>
    <KeyValuePair title="Chassis Account">{chassis.customer?.name}</KeyValuePair>
    <KeyValuePair title="Chassis Status">{chassis.unitStatus}</KeyValuePair>
    <KeyValuePair title="Chassis License Plate">{chassis.licensePlate}</KeyValuePair>
    <KeyValuePair title="Flatbed">{!chassis.flatbed ? 'NO' : 'YES'}</KeyValuePair>
    <KeyValuePair title="Owner Chassis">{!chassis.ownerChassis ? 'NO' : 'YES'}</KeyValuePair>
  </>
);

const GensetReview: FC<{ genset: Genset }> = ({ genset }) => (
  <>
    <KeyValuePair title="Genset Number">{genset.unitNumber}</KeyValuePair>
    <KeyValuePair title="Genset Size Type">{genset.sizeType}</KeyValuePair>
    <KeyValuePair title="Genset Account">{genset.customer?.name}</KeyValuePair>
    <KeyValuePair title="Genset Status">{genset.unitStatus}</KeyValuePair>
  </>
);

const ReviewTransactionForm: FC = () => {
  const navigate = useNavigate();

  const { damages } = useDamage();
  const { tires } = useTireInspections();
  const { isReefer } = useSizeTypes();
  const { bulk, transaction, container, chassis, genset, status, stack, interchangeEmails, setInterchangeEmails, saveAsDraft, saveTransaction } =
    useNewTransaction();

  const inputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<SignatureCanvas>(null);
  const [signature, setSignature] = useState('');

  const handleResetSignature = useCallback(() => {
    inputRef.current?.setCustomValidity('Signature Required');
    canvasRef.current?.clear();
    setSignature('');
  }, [setSignature]);

  const handleSetSignature = useCallback(() => {
    inputRef.current?.setCustomValidity('');
    const dataUrl = canvasRef.current?.toDataURL();
    setSignature(dataUrl ?? '');
  }, [setSignature]);

  const handleSubmit: FormEventHandler = useCallback(
    e => {
      e.preventDefault();
      saveTransaction(signature);
    },
    [signature, saveTransaction]
  );

  useEffect(() => {
    inputRef.current?.setCustomValidity('Signature Required');
  }, []);

  return (
    <form id="review-transaction" className="d-flex flex-column w-100" onSubmit={handleSubmit}>
      <div className="d-flex justify-content-between p-3 transaction-header">
        <div className="d-flex g-3">
          <h1 className="text-primary-dark font-weight-bold">REVIEW TRANSACTION</h1>
          <div className="d-flex g-2">
            {!transaction.inbound ? (
              <label className="status gate-out">GATE OUT</label>
            ) : (
              <label className="status gate-in">GATE IN</label>
            )}
            {bulk && <label className="status bulk">BULK</label>}
          </div>
        </div>
        <button
          type="button"
          className="action btn-outline d-flex g-2"
          onClick={() => navigate(`/gate/new?inbound=${transaction.inbound}&bulk=${bulk}`)}
        >
          EDIT
          <span className="material-icons">edit</span>
        </button>
      </div>
      <div className="d-flex flex-column flex-fill form-content p-3">
        <div className="card-primary px-3">
          <div className="card-header">
            <h4>EQUIPMENT</h4>
          </div>
          <div className="card-body">
            <KeyValuePair title="Date Completed">
              {!transaction.completedDate ? null : new Date(transaction.completedDate).toLocaleDateString()}
            </KeyValuePair>
            <KeyValuePair title="Booking Number">{transaction.booking?.bookingNumber}</KeyValuePair>
            <KeyValuePair title="Redelivery Number">{transaction.redeliveryNumber}</KeyValuePair>
            <KeyValuePair title="Notes">{transaction.notes}</KeyValuePair>

            <KeyValuePair title="Container Number">{container.unitNumber}</KeyValuePair>
            <KeyValuePair title="Container Size Type">{container.sizeType}</KeyValuePair>
            <KeyValuePair title="Container Account">{container.customer?.name}</KeyValuePair>
            <KeyValuePair title="Container Status">{container.unitStatus}</KeyValuePair>

            {/* the catalog's reefer flag decides; the old regex missed 40RH and 45R1 */}
            {isReefer(container.sizeType) && (
              <>
                <KeyValuePair title="Temperature">{container.temperature}</KeyValuePair>
                <KeyValuePair title="Humidity">{container.humidity}</KeyValuePair>
                <KeyValuePair title="Ventilation">{container.ventilation}</KeyValuePair>
              </>
            )}
            {!!genset.sizeType && genset.sizeType.includes('GS') && (
              <KeyValuePair title="Fuel Level">{genset?.fuelLevel ?? ''}</KeyValuePair>
            )}

            {container.loaded && (
              <>
                <KeyValuePair title="Seal #1">{container.seal0}</KeyValuePair>
                <KeyValuePair title="Seal #2">{container.seal1}</KeyValuePair>
                <KeyValuePair title="Seal #3">{container.seal2}</KeyValuePair>
                <KeyValuePair title="Hazards">{!container.hazard ? 'NO' : 'YES'}</KeyValuePair>
              </>
            )}

            <ChassisReview chassis={chassis} />

            {(stack as any).filter(isChassis).map((chassis: Chassis) => (
              <ChassisReview chassis={chassis} />
            ))}

            <GensetReview genset={genset} />

            {(stack as any).filter(isGenset).map((genset: Genset) => (
              <GensetReview genset={genset} />
            ))}

            <KeyValuePair title="Trucking Company">{transaction.truckingCompany?.name}</KeyValuePair>
            <KeyValuePair title="Truck Number">{transaction.truckNumber}</KeyValuePair>
            <KeyValuePair title="Driver Name">{transaction.driverName}</KeyValuePair>
            <KeyValuePair title="CDL Number">{transaction.cdlNumber}</KeyValuePair>
            <KeyValuePair title="Truck License Plate">{transaction.licensePlate}</KeyValuePair>
            {/* imageAttachments locationId */}
          </div>
        </div>
        {!!damages.length && (
          <div className="card-primary px-3">
            <div className="card-header">
              <h4>INSPECTION</h4>
            </div>
            <div className="card-body" id="damages">
              {damages.map((damage, i, arr) =>
                !damage.equipmentType ? null : (
                  <>
                    <KeyValuePair title="Equipment">{damage.equipmentType}</KeyValuePair>
                    {damage.equipmentType === 'CHASSIS' && (
                      <KeyValuePair title="System">{damage.location}</KeyValuePair>
                    )}
                    {damage.equipmentType !== 'CHASSIS' && (
                      <KeyValuePair title="Location">{damage.location}</KeyValuePair>
                    )}
                    {'part' in damage && <KeyValuePair title="Component">{String(damage.part)}</KeyValuePair>}
                    {'subLocation' in damage && <KeyValuePair title="Location">{damage.subLocation}</KeyValuePair>}
                    {'damageType' in damage && <KeyValuePair title="Type">{damage.damageType}</KeyValuePair>}
                    {'notes' in damage && <KeyValuePair title="Notes">{damage.notes}</KeyValuePair>}
                    {!!arr[i + 1] && <Divider />}
                  </>
                )
              )}
            </div>
          </div>
        )}
        <div className="card-primary px-3">
          <div className="card-header">
            <h4>TIRES</h4>
          </div>
          <div className="card-body">
            {Object.entries(tires).reduce(
              (acc, [key, val], i) =>
                !val
                  ? acc
                  : [
                      ...acc,
                      <KeyValuePair key={i} title={key.replace(/([a-z])([A-Z])/g, '$1 $2')}>
                        {val.damageType}
                        {[val?.measurement0, val?.measurement1, val?.measurement2]
                          .filter((measurement): measurement is number => typeof measurement === 'number')
                          .map((measurement, i) => (
                            <span className="ml-3 ws-n3" key={i}>
                              {measurement} / 32
                            </span>
                          ))}
                        <span>{val.damageType && val.notes ? ' | ' + val.notes : val.notes ? val.notes : ''}</span>
                      </KeyValuePair>
                    ],
              [] as JSX.Element[]
            )}
          </div>
        </div>
        <div className="card-primary px-3">
          <div className="card-header">
            <h4>INTERCHANGE EMAILS</h4>
          </div>
          <div className="card-body">
            <EmailSelect
              email={interchangeEmails}
              setEmail={setInterchangeEmails}
            />
          </div>
        </div>
        <div className="card-primary px-3">
          <div className="card-header">
            <h4>SIGNATURE</h4>
          </div>
          <div className="card-body">
            <div id="signature">
              <SignatureCanvas canvasProps={{ width: 540, height: 160 }} onEnd={handleSetSignature} ref={canvasRef} />
              <Divider color="info" />
              <Close />
              <Button disabled={!signature} onClick={handleResetSignature}>
                CLEAR
              </Button>
              <input ref={inputRef /* hidden canvas validity - require signature */} />
              <em>{transaction.driverName}</em>
            </div>
          </div>
        </div>
      </div>
      <div className="p-3 transaction-footer">
        <div className="form-actions grid-3">
          <CancelConfirmation />
          <button
            className="action btn-outline"
            type="button"
            onClick={saveAsDraft}
            disabled={status === TransactionStatus.WAITING}
            data-cy="save-draft-button"
          >
            Save Draft
          </button>
          <button
            className="action btn-primary"
            type="submit"
            disabled={status === TransactionStatus.WAITING}
            data-cy="submit-transaction-button"
          >
            Submit
          </button>
        </div>
      </div>
    </form>
  );
};

const ReviewTransaction: FC = () => <ReviewTransactionForm />;

export default ReviewTransaction;
